import {
  useEffect,
  useState,
  createContext,
  useContext,
  useRef,
  JSX,
} from 'react';
import * as signalR from '@microsoft/signalr';
import { jwtDecode } from 'jwt-decode';
import { useUserUpdaterProvider } from '../user_context/UserUpdaterProvider';
import { useEmergencyUpdaterProvider } from '../emergency_context/EmergencyUpdaterProvider';
import { useOrganizationUpdaterProvider } from '../organization_context/OrganizationProvider';
import { useUserContext } from '../user_context/UserProvider';
import { getSelf, hydrateObservedMap } from '@/repository/UserDAO';
import {
  getOrganization,
  getMiniOrganization,
} from '@/repository/OrganizationDAO';
import SignalROnFunctions from '@/utils/signal_r/SignalROnFunctions';
import { useAuth0 } from '@auth0/auth0-react';
import { LoadingNavBar } from '@/components/navbar/LoadingNavBar';
import {
  isAdmin,
  isMobileUser,
} from '@/components/routing/UserRoutes/AcceptedRoles';
import SignUp from '@/views/UserViews/sign_up/SignUp';
import { useLoading } from '../loading_context/LoadingContext';
import {
  invokeOrganizationSRGroup,
  invokeUserSRGroup,
  invokeAppVersion,
  negotiateSignalR,
  invokeOrganizationContactsSRGroup,
  invokeOrganizationAdminSRGroup,
} from '@/repository/SignalRDAO';
import { useAppVersionProvider } from '../app_version_context/AppVersionProvider';
import ErrorComponent from '@/components/utils/ErrorComponent';
import { useConnectivity } from '../connectivity_context/ConnectivityProvider';
import SignalRErrorCodes from '@/utils/error_codes/SignalRErrorCodes';
import APIErrorCodes from '@/utils/error_codes/APIErrorCodes';
import {
  getUserStatus,
  setGroupUserStatuses,
  updateUserStatus,
} from '../group_user_context/OrgGroupUserStatusStore';
import ConnectionStatus from '@/enums/ConnectionStatus';
import { UserHistory } from '@/models/User/UserHistory';
import { OrgUser } from '@/models/User/OrgUser';
import {
  OrgJoinRequestItem,
  UserStatusPing,
} from '../../../generated-client/models';

type SignalRContextType = {
  connection: signalR.HubConnection | undefined;
};

/**
 * Contains necessary information for a SignalR client to connect to SignalR Service.
 */
export interface SignalRConnectionInfo {
  /** The URL for a client to connect to SignalR Service. */
  Url: string;

  /** The access token for a client to connect to SignalR Service. */
  AccessToken: string;
}

export const SignalRContext = createContext<SignalRContextType>({
  connection: undefined,
});

/**
 * This component controls the SignalR connection for a non-Safeguard permission user
 */

export const SignalRListener = ({ children }: { children: JSX.Element }) => {
  const MAX_RETRIES = 10;
  const RESPONSE_WAIT_MS = 5000;

  const {
    updateOrgUser,
    updateUserPrivateSettings,
    updateUserHistory,
    orgUser,
    userRole,
  } = useUserUpdaterProvider();
  const { updateEmergencies, updateEmergencyMessages } =
    useEmergencyUpdaterProvider();
  const {
    organization,
    updateOrganization,
    setUser,
    getUsersGroup,
    setJoinRequests,
    loadPvdConfigs,
    loadEmergencyConfigs,
  } = useOrganizationUpdaterProvider();

  // Single ref holding latest organization and getUsersGroup to avoid stale closures in SignalR handlers
  const orgCtxRef = useRef({ organization, getUsersGroup });

  useEffect(() => {
    orgCtxRef.current = { organization, getUsersGroup };
  }, [organization, getUsersGroup]);

  const {
    bearerToken,
    userExists,
    isSafeguardAdmin,
    safeguardView,
    refreshEntitlements,
    isSafeguardManufacturingUser,
    isSafeguardDev,
  } = useUserContext();
  const { setNormalUserSignalrStatus } = useLoading();
  const { inMaintenance, updateWebVersion } = useAppVersionProvider();
  const { isAuthenticated, isLoading, loginWithRedirect, logout } = useAuth0();
  const { isOnline, isOnlineRef } = useConnectivity();
  const [connection, setConnection] = useState<signalR.HubConnection>();
  const [hasExceededMaxRetries, setHasExceededMaxRetries] = useState(false);
  const [signalRNegotiateError, setSignalRNegotiateError] = useState(false);
  const [lastConnectionAttempt, setLastConnectionAttempt] = useState(0);
  const [shouldRetry, setShouldRetry] = useState(false);
  const [signalRData, setSignalRData] = useState<SignalRConnectionInfo | null>(
    null,
  );
  const [tokenExpiration, setTokenExpiration] = useState<number>(0);
  const CONNECTION_ATTEMPT_INTERVAL = 10000;

  const isTokenExpired = () => {
    if (!tokenExpiration) return true;
    return Date.now() >= tokenExpiration - 30000;
  };

  const refreshSignalRToken = async () => {
    try {
      const signalRNegotiateResponse = await negotiateSignalR(bearerToken);
      if (!signalRNegotiateResponse.ok) {
        setSignalRNegotiateError(true);
        return false;
      }

      // set the token expiration based on the new token
      const newSignalRData: SignalRConnectionInfo =
        await signalRNegotiateResponse.json();

      // Override URL if demo mode is enabled and demo URL is provided
      if (
        import.meta.env.VITE_USE_DEMO_MODE === 'true' &&
        import.meta.env.VITE_DEMO_MODE_SIGNAL_R_HUB_URL
      ) {
        newSignalRData.Url = import.meta.env.VITE_DEMO_MODE_SIGNAL_R_HUB_URL;
        console.log('Using demo mode SignalR URL:', newSignalRData.Url);
      }

      const decodedToken = jwtDecode(newSignalRData.AccessToken);
      setTokenExpiration((decodedToken?.exp ?? 0) * 1000);
      setSignalRData(newSignalRData);
      return newSignalRData;
    } catch (error) {
      console.error('Error negotiating SignalR connection:', error);
      setSignalRNegotiateError(true);
      return false;
    }
  };

  //If not authenticated, redirect to login
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      loginWithRedirect();
      return;
    }
  }, [isAuthenticated, isLoading, loginWithRedirect]);

  //On successful connection and retrieval of org code, send user data
  useEffect(() => {
    if (connection) {
      if (connection.state === signalR.HubConnectionState.Connected) sendUser();
    }
  }, [connection]);

  // Remove connection entirely if user goes offline
  useEffect(() => {
    if (!isOnline.current) setConnection(undefined);
  }, [isOnline]);

  // Build a new connection
  useEffect(() => {
    if (bearerToken && !inMaintenance && isOnlineRef.current) {
      setConnection(undefined);
      buildConnection();
      setShouldRetry(false);
    }
  }, [bearerToken, inMaintenance, isOnlineRef, shouldRetry]);

  useEffect(() => {
    if (!orgUser || !userRole) return;

    const adminGetUserOrganizationAndSubscription = async () => {
      try {
        var organization = await getOrganization(orgUser.org_code);
        Object.entries(organization.groups).forEach(
          ([groupName, groupData]) => {
            invokeGroup(groupName, organization.org_code);
          },
        );

        updateOrganization(organization);
        refreshEntitlements(organization);
      } catch (e) {
        console.error('Error fetching organization');
      }
    };

    const mobileUserGetUserMiniOrganization = async () => {
      try {
        var organization = await getMiniOrganization(orgUser.org_code);
        updateOrganization(organization);
        refreshEntitlements(organization);
      } catch (error) {
        console.warn('Error fetching organization');
        console.error(error);
      }
    };
    if (isMobileUser(userRole)) {
      mobileUserGetUserMiniOrganization();
    } else {
      adminGetUserOrganizationAndSubscription();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgUser, userRole]);

  useEffect(() => {
    if (
      connection &&
      connection.state === signalR.HubConnectionState.Disconnected
    ) {
      if (
        ((!isSafeguardAdmin &&
          !isSafeguardManufacturingUser &&
          !isSafeguardDev) ||
          !safeguardView) &&
        userExists &&
        isOnline
      )
        startConnection();
      else if (safeguardView) stopConnection();
    }
  }, [
    isSafeguardAdmin,
    isSafeguardManufacturingUser,
    isSafeguardDev,
    userExists,
    connection,
    safeguardView,
    isOnline,
  ]);

  useEffect(() => {
    if (orgUser) {
      if (isAdmin(orgUser.role)) invokeOrganization(orgUser.org_code);
    }
  }, [orgUser]);

  const buildConnection = async () => {
    setConnection(undefined);
    const now = Date.now();
    if (now - lastConnectionAttempt < CONNECTION_ATTEMPT_INTERVAL) return;
    setLastConnectionAttempt(now);

    // Always refresh token on initial connection or if expired
    let currentSignalRData = signalRData;
    if (!currentSignalRData || isTokenExpired()) {
      const refreshResult = await refreshSignalRToken();
      if (!refreshResult) return;
      currentSignalRData = refreshResult;
    }

    // Ensure we have valid signalRData before proceeding
    if (!currentSignalRData?.Url) {
      console.error('No valid SignalR URL available');
      setSignalRNegotiateError(true);
      return;
    }

    if (
      !connection ||
      connection.state === signalR.HubConnectionState.Disconnected
    ) {
      const newConnection = new signalR.HubConnectionBuilder()
        .withUrl(currentSignalRData.Url, {
          accessTokenFactory: async () => {
            // Check token expiration before returning
            if (isTokenExpired()) {
              const refreshResult = await refreshSignalRToken();
              if (refreshResult) {
                return refreshResult.AccessToken;
              }
              return currentSignalRData.AccessToken;
            }
            return currentSignalRData.AccessToken;
          },
        })
        .withAutomaticReconnect([0, 2000, 10000, 30000])
        .build();

      setConnection(newConnection);
      setSignalRNegotiateError(false);
    }
  };

  const startConnection = async () => {
    if (isOnlineRef.current && connection) {
      try {
        connection.on(SignalROnFunctions.RECEIVE_USER_HISTORY, (data) => {
          updateUserHistory(data);
        });
        connection.on(SignalROnFunctions.RECEIVE_ORG_USER, (data) => {
          updateOrgUser(data);
        });
        connection.on(SignalROnFunctions.RECEIVE_VERSION_WEB_APP, (data) => {
          updateWebVersion(data);
        });
        connection.on(
          SignalROnFunctions.RECEIVE_USER_PRIVATE_SETTINGS,
          (data) => {
            updateUserPrivateSettings(data);
          },
        );
        connection.on(SignalROnFunctions.RECEIVE_USER_DESTROYED, () => {
          logout({
            logoutParams: {
              returnTo: window.location.origin,
            },
          });
        });
        connection.on(SignalROnFunctions.RECEIVE_EMERGENCY, (data) => {
          updateEmergencies(data);
        });
        connection.on(SignalROnFunctions.RECEIVE_EMERGENCY_MESSAGE, (data) => {
          updateEmergencyMessages(data);
        });

        connection.on(
          SignalROnFunctions.RECEIVE_ORG_GROUP_USER_STATUSES,
          (data) => {
            // Assume data has a group_name property or id that identifies the group
            const groupName = data.group_name || data.id;
            if (groupName) {
              setGroupUserStatuses(groupName, data);
              console.log('stored statuses for group', groupName);
            }
          },
        );

        // Listener for single user status updates.
        // When a user becomes active we instantly update their
        // status in the store -- useful for demos where people expect "live" updates
        connection.on(
          SignalROnFunctions.RECEIVE_USER_STATUS,
          (data: UserStatusPing) => {
            var ping = data;
            try {
              console.log('updating status for user', ping.user_auth_id);
              const { organization, getUsersGroup } = orgCtxRef.current;

              // when receiving a disconnected_intentional status, retain the previous status
              if (
                ping.connection_status ===
                ConnectionStatus.DisconnectedIntentional
              ) {
                const existingPing = getUserStatus(
                  ping.user_auth_id,
                )?.userStatus;
                if (existingPing) {
                  existingPing.dto.connection_status =
                    ConnectionStatus.DisconnectedIntentional;
                  ping = existingPing.dto;
                }
              }
              if (organization) {
                const usersGroup = getUsersGroup(
                  organization,
                  ping.user_auth_id,
                );
                updateUserStatus(ping, usersGroup);
              }
            } catch (error) {
              console.error('Error updating user status:', error);
            }
          },
        );

        connection.on(
          SignalROnFunctions.ADMIN_RECEIVE_ORG_USER_UPDATED,
          (user) => {
            setUser(user);
          },
        );

        connection.on(SignalROnFunctions.RECEIVE_ORGANIZATION, (data) => {
          refreshEntitlements(data);
          updateOrganization(data);
        });

        connection.on(
          SignalROnFunctions.RECEIVE_ORG_JOIN_REQUESTS,
          (
            data: { join_requests?: OrgJoinRequestItem[] | null } | undefined,
          ) => {
            setJoinRequests(data?.join_requests ?? []);
          },
        );

        connection.on(SignalROnFunctions.RECEIVE_MINI_ORG, (data) => {
          refreshEntitlements(data);
        });

        connection.on(SignalROnFunctions.RECEIVE_PVD_CONFIG_CHANGED, () => {
          loadPvdConfigs?.();
        });

        connection.on(
          SignalROnFunctions.RECEIVE_EMERGENCY_CONFIG_CHANGED,
          () => {
            loadEmergencyConfigs?.();
          },
        );

        connection.onreconnected(async () => {
          setNormalUserSignalrStatus(connection.state);
          // Re-run the same resync as first connect instead of reusing the
          // closed-over `orgUser`/`isAdmin` check above: this handler is
          // registered on `connection` while building it, which can happen
          // before `orgUser` is ever set (e.g. the connection was built on
          // first page load). That stale-null closure meant reconnects
          // never rejoined the organization/group SignalR groups or
          // refetched the org, so a tab left open past a socket drop (idle
          // timeout, sleep, network blip) silently stopped receiving
          // ReceiveOrganization/org-member updates for the rest of the
          // session. `sendUser` fetches the current user fresh and calls
          // `updateOrgUser` with a new object, which re-triggers the
          // `[orgUser]`/`[orgUser, userRole]` effects that join the org
          // group and refetch the organization.
          //
          // `invokeUser` and `invokeWebAppVersionListener` don't catch their
          // own errors (unlike `sendUser` and `joinAdminDispatchGroup`), so
          // without this try/catch a single failed group-join here (e.g. a
          // transient 401 mid-reconnect) would reject this handler's promise
          // and skip every call after it, silently leaving the rest of the
          // resync undone.
          try {
            await sendUser();
            await invokeUser();
            await joinAdminDispatchGroup();
            await invokeWebAppVersionListener();
          } catch (error) {
            console.error('Error resyncing after SignalR reconnect:', error);
          }
        });
        connection.onreconnecting(() => {
          setNormalUserSignalrStatus(connection.state);
        });
        connection.onclose(async () => {
          setShouldRetry(true);
          setNormalUserSignalrStatus(connection.state);
          if (isOnlineRef.current && !safeguardView) {
            setTimeout(async () => {
              await buildConnection();
            }, 5000);
          }
        });

        try {
          await connection.start();
        } catch (startError) {
          setShouldRetry(true);
          setConnection(undefined);
          return;
        } finally {
          setNormalUserSignalrStatus(connection.state);
        }
        if (connection) {
          if (connection.state === signalR.HubConnectionState.Connected)
            sendUser();
        }
        await invokeUser();
        await joinAdminDispatchGroup();
        await invokeWebAppVersionListener();
      } catch (err) {
        setShouldRetry(true);
      }
    }
  };

  const stopConnection = async () => {
    try {
      if (connection) {
        if (connection.state === signalR.HubConnectionState.Connected)
          await connection.stop();
      }
    } catch (error) {
      console.error('Error disconnecting SignalR:', error);
    }
  };

  const invokeOrganization = async (organizationCode: string) => {
    if (!connection?.connectionId) {
      console.warn('The connection id is not set');
      return;
    }
    await invokeOrganizationSRGroup(
      true,
      connection?.connectionId,
      organizationCode,
      bearerToken,
    );
  };

  const invokeUser = async () => {
    if (!connection?.connectionId) {
      console.warn('The connection id is not set');
      return;
    }
    await invokeUserSRGroup(true, connection?.connectionId, bearerToken);
  };

  /**
   * Connects the user to the organization admin SignalR group.
   * This group receives updates on user settings related to the devices
   *
   */
  const joinAdminDispatchGroup = async () => {
    try {
      if (!connection?.connectionId) {
        console.warn('The connection id is not set');
        return;
      }
      await invokeOrganizationAdminSRGroup(
        true,
        connection?.connectionId,
        bearerToken,
      );
    } catch (err) {
      // We should only join the admin dispatch group if the user is an admin or dispatch,
      // but the role depends on the orgUser being set, which may not have happened yet.
      // Currently, the this file is written to just join groups during connection setup,
      // so we may attempt to join this group even if the user is not an admin/dispatch.
      // We would need to restructure the connection setup logic to ensure the orgUser is set
      // before joining groups to avoid this error entirely.
      console.error('Error joining organization admin SR group:', err);
    }
  };

  const invokeGroup = async (groupName: string, orgCode: string) => {
    if (!connection?.connectionId) {
      console.warn('The connection id is not set');
      return;
    }
    await invokeOrganizationContactsSRGroup(
      true,
      connection?.connectionId,
      orgCode,
      groupName,
      bearerToken,
    );
  };

  const invokeWebAppVersionListener = async () => {
    if (!connection?.connectionId) {
      console.warn('The connection id is not set');
      return;
    }
    await invokeAppVersion(true, connection.connectionId, bearerToken);
  };

  const sendUser = async (retryCount = 0) => {
    try {
      var user = await getSelf();

      const userHistory: UserHistory = {
        auth_id: user.user_history.auth_id,
        id: user.user_history.id,
        object_type: user.user_history.object_type,
        org_code: user.user_history.org_code,
        unique_key: user.user_history.unique_key,
        _etag: user.user_history._etag,
        _ts: user.user_history._ts,
        observed_emergencies: hydrateObservedMap(
          user.user_history.observed_emergencies,
        ),
        read_inbox_message_names: user.user_history.read_inbox_message_names,
      };

      updateOrgUser(new OrgUser(user.org_user));
      updateUserHistory(userHistory);
      updateUserPrivateSettings(user.user_private_settings);
    } catch (error) {
      setTimeout(() => {
        if (retryCount < MAX_RETRIES) {
          sendUser(retryCount + 1);
        } else {
          setHasExceededMaxRetries(true);
        }
      }, RESPONSE_WAIT_MS);
    }
  };

  if (userExists === false) {
    return (
      <>
        <LoadingNavBar />
        <SignUp />
      </>
    );
  }

  if (hasExceededMaxRetries) {
    return (
      <>
        <LoadingNavBar />
        <ErrorComponent error={APIErrorCodes.EXCEEDED_MAX_RETRIES} />
      </>
    );
  }

  if (signalRNegotiateError) {
    return (
      <>
        <LoadingNavBar />
        <ErrorComponent error={SignalRErrorCodes.SIGNALR_NEGOTIATE_ERROR} />
      </>
    );
  }

  return (
    <SignalRContext.Provider value={{ connection }}>
      {children}
    </SignalRContext.Provider>
  );
};

export const useSignalR = () => {
  return useContext(SignalRContext);
};
